import { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Fab
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SchoolIcon from '@mui/icons-material/School'

const GLOSSARY = {
  'Key Metrics': [
    {
      term: 'kW/Ton',
      definition: 'Kilowatts per ton of cooling. Measures how much electricity is needed to produce one ton of cooling. Lower is better. Industry standard efficiency metric.',
      example: '1.2 kW/Ton means 1.2 kW of electricity produces 1 ton (12,000 BTU/hr) of cooling.',
      range: 'Typical range: 0.6-1.8 kW/Ton'
    },
    {
      term: 'COP (Coefficient of Performance)',
      definition: 'Ratio of cooling output to electrical input. Higher is better. COP = 3.517 / kW/Ton.',
      example: 'COP of 4.0 means 4 units of cooling per 1 unit of electricity.',
      range: 'Typical range: 2.0-6.0'
    },
    {
      term: 'EER (Energy Efficiency Ratio)',
      definition: 'BTU/hr of cooling per watt of electricity. Higher is better. EER = 12 / kW/Ton.',
      example: 'EER of 12 means 12 BTU/hr cooling per watt.',
      range: 'Typical range: 8-20'
    },
    {
      term: 'Efficiency Improvement %',
      definition: 'Percentage reduction in energy consumption for the same cooling output after treatment.',
      example: '25% improvement means the system uses 25% less electricity for the same cooling.',
      range: 'Study range: 5-45%'
    },
    {
      term: 'Delta T (\u0394T)',
      definition: 'Temperature difference across a coil or system. A key performance indicator — higher Delta T typically means better heat transfer.',
      example: 'A chiller with 10\u00b0F Delta T across the evaporator is transferring heat effectively.',
      range: 'Typical evaporator: 8-12\u00b0F'
    },
    {
      term: 'Tonnage (Cooling Tons)',
      definition: 'Unit of cooling capacity. One ton = 12,000 BTU/hr, roughly the energy needed to melt one ton of ice in 24 hours.',
      example: 'A 100-ton chiller can produce 1,200,000 BTU/hr of cooling.',
      range: 'Corpus range: 2-3000 tons'
    },
    {
      term: 'Subcooling / Superheat',
      definition: 'Refrigerant cycle performance indicators. Subcooling is how far liquid refrigerant is cooled below condensing temperature. Superheat is how far vapor is heated above evaporating temperature.',
      example: 'Proper subcooling (8-12\u00b0F) and superheat (10-15\u00b0F) indicate a well-charged, efficient system.',
      range: 'Target: 8-15\u00b0F each'
    }
  ],
  'Equipment Types': [
    {
      term: 'Chiller',
      definition: 'Large cooling system that produces chilled water for building HVAC. Typically 50-2000+ tons capacity.',
      example: 'Commercial buildings, data centers, hospitals often use chillers.',
      range: 'Capacity: 50-3000 tons'
    },
    {
      term: 'RTU (Rooftop Unit)',
      definition: 'Self-contained HVAC unit installed on rooftops. Common in retail, restaurants, small commercial.',
      example: 'Fast food restaurants typically have 2-5 RTUs.',
      range: 'Capacity: 2-50 tons'
    },
    {
      term: 'CRAC (Computer Room Air Conditioner)',
      definition: 'Precision cooling for data centers and server rooms. Maintains tight temperature/humidity control.',
      example: 'Data centers use CRACs to keep servers at optimal temperature.',
      range: 'Capacity: 5-100 tons'
    },
    {
      term: 'DX Split System',
      definition: 'Direct expansion system with separate indoor/outdoor units connected by refrigerant lines.',
      example: 'Residential AC and small commercial spaces.',
      range: 'Capacity: 1-20 tons'
    }
  ],
  'Refrigerants': [
    {
      term: 'R-22 (HCFC)',
      definition: 'Legacy refrigerant being phased out due to ozone depletion. Still common in older equipment.',
      example: 'Equipment manufactured before 2010 often uses R-22.',
      range: '75% of studies use R-22'
    },
    {
      term: 'R-410A (HFC)',
      definition: 'Modern refrigerant replacing R-22. Higher pressure, no ozone impact but high GWP.',
      example: 'Most new residential and commercial equipment uses R-410A.',
      range: '10% of studies use R-410A'
    },
    {
      term: 'R-134a',
      definition: 'Common in automotive AC and large chillers. Lower pressure than R-410A.',
      example: 'Centrifugal chillers often use R-134a.',
      range: '9% of studies use R-134a'
    }
  ],
  'Measurement Standards': [
    {
      term: 'IPMVP Option B',
      definition: 'International Performance Measurement and Verification Protocol. Option B isolates the equipment being measured and compares before/after performance.',
      example: 'All PE-certified studies in this corpus use IPMVP Option B methodology.',
      range: '100% IPMVP compliant'
    },
    {
      term: 'PE Certification',
      definition: 'Professional Engineer certification. PE stamp means a licensed engineer verified the measurements and calculations.',
      example: 'PE certification adds legal accountability to the measurements.',
      range: '100% PE certified'
    },
    {
      term: 'M&V (Measurement & Verification)',
      definition: 'Process of using measurements to reliably determine actual savings from energy efficiency projects.',
      example: 'M&V ensures claimed savings are real, not estimated.',
      range: 'Standard practice for ECMs'
    },
    {
      term: 'ASHRAE Standard 37',
      definition: 'Standard methods for testing HVAC equipment performance under controlled, repeatable conditions. Defines how to measure capacity and efficiency.',
      example: 'Lab studies in the corpus follow ASHRAE 37 test procedures for kW/Ton measurement.',
      range: 'Industry standard'
    },
    {
      term: 'Baseline Adjustment',
      definition: 'Modification of baseline energy data to account for differences in weather, occupancy, or operating conditions between pre- and post-treatment periods.',
      example: 'If summer baseline was measured at 95\u00b0F but post-treatment at 90\u00b0F, the baseline is adjusted to normalize the comparison.',
      range: 'Required by IPMVP'
    },
    {
      term: 'ISO 17025',
      definition: 'International standard for laboratory testing competence. Ensures labs have proper calibration, procedures, and quality systems.',
      example: 'Independent labs validating PermaFrost NMR hold ISO 17025 accreditation.',
      range: '6+ accredited labs'
    }
  ],
  'Financial Terms': [
    {
      term: 'Annual Savings',
      definition: 'Projected yearly cost reduction based on measured efficiency improvement and local utility rates.',
      example: '$15,000 annual savings = 100,000 kWh saved × $0.15/kWh',
      range: 'Study range: $2K-$150K'
    },
    {
      term: 'Payback Period',
      definition: 'Time required for energy savings to equal the treatment cost. Shorter is better.',
      example: '12-month payback means treatment cost is recovered in 1 year.',
      range: 'Typical: 6-18 months'
    },
    {
      term: 'ROI (Return on Investment)',
      definition: 'Percentage return comparing savings to cost. ROI = (Savings - Cost) / Cost \u00d7 100%',
      example: '200% ROI means you get back 3\u00d7 what you invested.',
      range: 'Typical: 100-400%'
    },
    {
      term: 'NPV (Net Present Value)',
      definition: 'Present value of future savings minus the initial investment, discounted at a required rate of return. Positive NPV means the investment creates value.',
      example: '$50K NPV at 8% discount rate means the project creates $50K in value above the cost of capital.',
      range: 'Decision rule: NPV > 0'
    },
    {
      term: 'IRR (Internal Rate of Return)',
      definition: 'The discount rate that makes NPV equal zero. Compare IRR to your hurdle rate — if IRR exceeds the hurdle, the investment is worthwhile.',
      example: 'IRR of 45% vs. 10% hurdle rate = strong investment.',
      range: 'Typical: 30-80%'
    },
    {
      term: 'CapEx vs OpEx',
      definition: 'Capital expenditure (CapEx) is capitalized on the balance sheet and depreciated. Operating expense (OpEx) hits the income statement immediately. Treatment that extends useful life or restores performance may qualify as CapEx under GAAP.',
      example: 'PermaFrost NMR may be capitalizable if it materially extends equipment life or restores original performance.',
      range: 'Consult your accountant'
    },
    {
      term: 'kWh Savings',
      definition: 'Reduction in electricity consumption measured in kilowatt-hours. Multiply by your utility rate to get dollar savings.',
      example: '100,000 kWh saved \u00d7 $0.12/kWh = $12,000 annual savings.',
      range: 'Varies by equipment size'
    },
    {
      term: 'Runtime Reduction',
      definition: 'Decrease in compressor or system runtime after treatment. Less runtime means lower energy use, reduced wear, and extended equipment life.',
      example: 'A chiller running 14 hrs/day reduced to 11 hrs/day = 21% runtime reduction.',
      range: 'Typical: 10-25%'
    },
    {
      term: 'Useful Life Extension',
      definition: 'Additional years of service from existing HVAC equipment due to reduced runtime, lower operating temperatures, and restored efficiency.',
      example: 'Delaying a $500K chiller replacement by 5 years has significant financial impact beyond energy savings.',
      range: 'Typical: 3-7 years'
    }
  ],
  'PermaFrost NMR': [
    {
      term: 'PermaFrost NMR\u00ae',
      definition: 'Nanomolecular refrigerant treatment designed to restore lost HVAC efficiency by improving heat transfer inside the refrigeration cycle.',
      example: 'Applied as a one-time treatment to existing equipment — no equipment modification required.',
      range: 'All refrigerant types'
    },
    {
      term: 'Efficiency Restoration',
      definition: 'Returning aging HVAC systems closer to original factory performance levels. Distinguished from "improvement" — the technology restores what degradation took away.',
      example: 'A 15-year-old chiller operating at 1.4 kW/Ton restored to 1.0 kW/Ton (near factory spec).',
      range: 'Avg: 29.8% restoration'
    },
    {
      term: 'Treatment Data',
      definition: 'Pre- and post-treatment performance measurements collected under IPMVP Option B methodology with PE certification.',
      example: 'Baseline kW, post-treatment kW, Delta T, runtime, and ambient conditions are all recorded.',
      range: '174 case studies'
    }
  ],
  'Industry & Deployment': [
    {
      term: 'Mission-Critical Facilities',
      definition: 'Sites where HVAC failure is unacceptable — data centers, telecom hubs, hospitals, financial trading floors.',
      example: 'Data centers require 99.999% uptime; HVAC failure causes immediate equipment shutdown.',
      range: '23% of corpus studies'
    },
    {
      term: 'Portfolio Rollout',
      definition: 'Deployment of treatment across multiple buildings or systems within an organization, typically after a successful pilot.',
      example: 'A bank piloting 3 branches, then rolling out to 200+ locations based on measured results.',
      range: 'Common deployment pattern'
    },
    {
      term: 'Field Measurements',
      definition: 'Real-world performance data collected from operating equipment, as opposed to lab testing. Includes power draw, temperature, runtime, and ambient conditions.',
      example: 'Field data is messier than lab data but reflects actual operating conditions.',
      range: '174 field studies in corpus'
    },
    {
      term: 'Independent Verification',
      definition: 'Third-party confirmation of performance data by licensed Professional Engineers, accredited labs, or university researchers with no financial interest in the outcome.',
      example: '113 Professional Engineers across 6+ independent labs have verified corpus data.',
      range: '113 PEs, 6+ labs'
    }
  ]
}

export function GlossaryPanel() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState('Key Metrics')

  const filteredGlossary = Object.entries(GLOSSARY).reduce((acc, [category, terms]) => {
    const filtered = terms.filter(t =>
      t.term.toLowerCase().includes(search.toLowerCase()) ||
      t.definition.toLowerCase().includes(search.toLowerCase())
    )
    if (filtered.length > 0) acc[category] = filtered
    return acc
  }, {})

  return (
    <>
      <Tooltip title="Glossary & Help">
        <Fab
          data-tour="tour-glossary"
          size="small"
          color="primary"
          onClick={() => setOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}
          aria-label="Open glossary"
        >
          <HelpOutlineIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 400 } } }}
      >
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon />
            <Typography variant="h6">HVAC Glossary</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: 'white' }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search terms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
          />
        </Box>

        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {Object.entries(filteredGlossary).map(([category, terms]) => (
            <Accordion
              key={category}
              expanded={expanded === category || search !== ''}
              onChange={() => setExpanded(expanded === category ? '' : category)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{category}</Typography>
                <Chip label={terms.length} size="small" sx={{ ml: 1 }} />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {terms.map((item, idx) => (
                    <Box key={idx}>
                      <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight={600}>
                          {item.term}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {item.definition}
                        </Typography>
                        {item.example && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            Example: {item.example}
                          </Typography>
                        )}
                        {item.range && (
                          <Chip label={item.range} size="small" variant="outlined" sx={{ mt: 1 }} />
                        )}
                      </ListItem>
                      {idx < terms.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.100', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Need more help? Click on any chart for interactive exploration, or use Ctrl+K to search the dashboard.
          </Typography>
        </Box>
      </Drawer>
    </>
  )
}

// Inline term tooltip for use in other components
export function TermTooltip({ term, children }) {
  const allTerms = Object.values(GLOSSARY).flat()
  const found = allTerms.find(t => t.term.toLowerCase() === term.toLowerCase())

  if (!found) return children

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="subtitle2">{found.term}</Typography>
          <Typography variant="body2">{found.definition}</Typography>
        </Box>
      }
      arrow
    >
      <span style={{ borderBottom: '1px dotted #666', cursor: 'help' }}>
        {children}
      </span>
    </Tooltip>
  )
}

export default GlossaryPanel
